import { useContext } from 'react'
import { ErrorMessage } from '@hookform/error-message'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoaderContext } from 'contexts/loader-context'
import { useSession } from 'contexts/session-context'
import Button from 'components/button'
import Input from 'components/input'
import { useForm } from 'react-hook-form'
import formServices from 'services/form-builder-services'
import ProfileService from 'services/profile-services'
import { AxiosError } from 'axios'
import { Label, ErrorInput } from 'theme/theme'
import textServices from 'services/text-services'
import { BackBtn } from 'views/login/text.style'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import CONFIGS from 'build/Config'
import Steps from '../components/Steps'

function Names({ state, nextStep, handleChange, prevStep, htmlSteps, setError }: any) {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({ resolver: formServices.formValidateErrors(yupResolver(formServices.formGenerateSchema(['firstName', 'lastName', 'nickName'])), 'sign up :: step 1', 'sign up'), reValidateMode: 'onSubmit' })
  const { setLoad } = useContext(LoaderContext)
  const [{ user }] = useSession()

  const validateNickName = async (nickName: string) => {
    setLoad(true)
    try {
      if (!CONFIGS.notShowFields?.includes('nickName') && nickName?.trim()?.toLowerCase() !== user?.nickName?.trim()?.toLowerCase() && nickName) {
        await ProfileService.validateNickName(nickName)
      }
      nextStep()
    } catch (error) {
      if ((error as AxiosError).isAxiosError) {
        const code = (error as AxiosError).response?.data.code ?? 9999;
        const message = (error as AxiosError).response?.data.message ?? 'NA';
        gtag('event', 'Click', {
          event_category: GTM_EVENT_CATEGORY,
          event_label: 'SINGUP - NickName existente',
          value: '10'
        })
        dlTrackEvent('error', {
          "error_type": "sign up",
          "error_code": code,
          "error_message": message,
          "section": "sign up :: step 1"
        })
        return setError((error as AxiosError).response?.data.code ?? 9999)
      }
    } finally {
      setLoad(false)
    }
  }

  const onFormSubmit = async (data: any) => {
    handleChange(data)
    const { nickName } = data
    gtag('event', 'step_1', {
      event_label: 'Registro - Email',
      method: 'Email'
    })
    dlTrackEvent('form_send', {
      element: 'siguiente',
      form_name: 'datos cuenta',
      section: 'sign up :: step 1'
    })
    if (user?.nickName?.trim()?.toLowerCase() !== nickName?.trim()?.toLowerCase()) {
      return validateNickName(nickName)
    }
    nextStep()
  }

  return (
    <div className="flex flex-col flex-1 md:pt-10">
      {
        !CONFIGS?.stepsNewVersion && (
          <div className="flex flex-row items-center justify-around w-full mb-4">{htmlSteps}</div>
        )
      }
      {
        CONFIGS?.stepsNewVersion && CONFIGS?.signUp?.stepsTop && (
          <Steps state={state} htmlSteps={htmlSteps} decrease={0.5} />
        )
      }
      <form
        className="flex h-full justify-between flex-col gap-4 mx-7 mb-7"
        onSubmit={handleSubmit(onFormSubmit)}
        noValidate
      >
        <div className="flex-1 flex flex-col gap-4">
          <div className="gap-2 flex flex-col input-container">
            <Label className="text-start text-2xl font-bold uppercase input-label">
              {textServices.findByText('profile/form/information/firstname/label', { es: '¿Cómo te llamas?', en: 'What is your name?' })}
            </Label>
            <Input
              className="text-center saira font-bold text-sm"
              type="text"
              placeholder={textServices.findByText(
                'profile/form/information/firstname/placeholder',
                { es: 'PON TU NOMBRE AQUÍ', en: 'PUT YOUR NAME HERE' }
              )}
              name="firstName"
              defaultValue={state?.firstName}
              {...register('firstName', {
                onBlur: (e) => {
                  dlTrackEvent('form_interaction', {
                    form_name: 'datos cuenta',
                    form_field: 'first name',
                    form_action: e.target.value ? 'complete' : 'empty',
                    section: 'sign up :: step 1'
                  })
                }
              })}
            />
            <ErrorInput className='input-error'>
              <ErrorMessage errors={errors} name="firstName" />
            </ErrorInput>
            <Input
              className="text-center saira font-bold text-sm"
              type="text"
              placeholder={textServices.findByText(
                'profile/form/information/lastname/placeholder',
                { es: 'PON TU APELLIDO AQUÍ', en: 'PUT YOUR LAST NAME HERE' }
              )}
              name="lastName"
              defaultValue={state?.lastName}
              {...register('lastName', {
                onBlur: (e) => {
                  dlTrackEvent('form_interaction', {
                    form_name: 'datos cuenta',
                    form_field: 'last name',
                    form_action: e.target.value ? 'complete' : 'empty',
                    section: 'sign up :: step 1'
                  })
                }
              })}
            />
            <ErrorInput className='input-error'>
              <ErrorMessage errors={errors} name="lastName" />
            </ErrorInput>
          </div>
          {!CONFIGS.notShowFields?.includes('nickName') && (
            <div className="gap-2 flex flex-col input-container">
              <Label className="text-start text-2xl font-bold uppercase input-label">
                {textServices.findByText('profile/form/information/nickname/label', { es: 'Apodo', en: 'Nickname' })}
              </Label>
              <Input
                className="text-center saira font-bold text-sm"
                type="text"
                placeholder={textServices.findByText(
                  'profile/form/information/nickname/placeholder',
                  { es: '¿COMO TE GUSTA QUE TE LLAMEN?', en: 'WHAT DO YOU LIKE TO BE CALLED?' }
                )}
                name="nickName"
                defaultValue={state?.nickName}
                {...register('nickName', {
                  onBlur: (e) => {
                    dlTrackEvent('form_interaction', {
                      form_name: 'datos cuenta',
                      form_field: 'nickname',
                      form_action: e.target.value ? 'complete' : 'empty',
                      section: 'sign up :: step 1'
                    })
                  }
                })}
              />
              <ErrorInput className='input-error'>
                <ErrorMessage errors={errors} name="nickName" />
              </ErrorInput>
            </div>
          )}
        </div>

        {
          CONFIGS?.stepsNewVersion && !CONFIGS?.signUp?.stepsTop && (
            <Steps state={state} htmlSteps={htmlSteps} decrease={0.5} />
          )
        }

        <Button>
          <p className="uppercase">{textServices.findByText('profile/form/information/names/button', { es: 'SIGUIENTE', en: 'CONTINUE' })}</p>
        </Button>
        <button
          type="button"
          className="cursor-pointe"
          onClick={() => {
            dlTrackEvent('user_interaction', {
              action: "click",
              element: 'volver',
              section: "sign up :: step 1"
            })
            gtag('event', 'Click', {
              event_category: GTM_EVENT_CATEGORY,
              event_label: 'MODIFICAR INFORMACION - Volver atras',
              value: '10'
            })
            prevStep()
          }}
        >
          <BackBtn className={`din text-center mt-3 hover:text-red-100 uppercase underline button-back`} style={{ fontSize: 10 }}>
            {textServices.findByText('backButton', { es: 'VOLVER', en: 'BACK TO' })}
          </BackBtn>
        </button>
      </form>
    </div>
  )
}

export default Names
